import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { maintenanceService } from '../../../lib/maintenanceService';

const PartsInventoryLookup = ({ gymId }) => {
  const [parts, setParts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showLowStock, setShowLowStock] = useState(false);

  useEffect(() => {
    if (gymId) {
      loadPartsInventory();
    }
  }, [gymId, showLowStock]);

  const loadPartsInventory = async () => {
    setLoading(true);
    try {
      const { data, error } = await maintenanceService?.getPartsInventory(gymId, {
        lowStock: showLowStock
      });

      if (!error) {
        setParts(data || []);
      }
    } catch (error) {
      console.error('Error loading parts inventory:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(parts?.map(p => p?.category)?.filter(Boolean))];

  const filteredParts = parts?.filter(part => {
    const matchesSearch = !searchQuery ||
      part?.part_name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      part?.part_number?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      part?.barcode?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || part?.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const getStockStatus = (part) => {
    if (part?.quantity_in_stock === 0) {
      return { label: 'Out of Stock', color: 'text-error', bg: 'bg-error/10' };
    } else if (part?.quantity_in_stock <= part?.minimum_stock_level) {
      return { label: 'Low Stock', color: 'text-warning', bg: 'bg-warning/10' };
    } else {
      return { label: 'In Stock', color: 'text-success', bg: 'bg-success/10' };
    }
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl border border-border p-12 text-center">
        <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
        <p className="text-sm text-muted-foreground">Loading parts inventory...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="bg-card rounded-xl border border-border p-4 space-y-4">
        <Input
          type="text"
          placeholder="Search by name, part number, or barcode..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          iconName="Search"
        />

        <div className="flex items-center gap-2 overflow-x-auto">
          {categories?.map((category) => (
            <button
              key={category}
              onClick={() => setCategoryFilter(category)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                categoryFilter === category
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-foreground hover:bg-muted/80'
              }`}
            >
              {category === 'all' ? 'All Categories' : category}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowLowStock(!showLowStock)}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              showLowStock
                ? 'bg-warning/10 text-warning border border-warning' :'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            <Icon name="AlertTriangle" size={16} />
            Low Stock Only
          </button>
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            onClick={loadPartsInventory}
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Parts List */}
      {filteredParts?.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Parts Found</h3>
          <p className="text-sm text-muted-foreground">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredParts?.map((part) => {
            const stockStatus = getStockStatus(part);

            return (
              <div
                key={part?.id}
                className="bg-card rounded-xl border border-border p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-base font-semibold text-foreground">{part?.part_name}</h3>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${stockStatus?.bg} ${stockStatus?.color}`}>
                        {stockStatus?.label}
                      </span>
                    </div>
                    {part?.category && (
                      <p className="text-sm text-muted-foreground mb-1">{part?.category}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  {part?.part_number && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Part Number</p>
                      <p className="text-sm font-medium text-foreground">{part?.part_number}</p>
                    </div>
                  )}
                  {part?.barcode && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Barcode</p>
                      <p className="text-sm font-medium text-foreground">{part?.barcode}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Quantity</p>
                    <p className="text-sm font-medium text-foreground">
                      {part?.quantity_in_stock} / {part?.minimum_stock_level} min
                    </p>
                  </div>
                  {part?.unit_cost && (
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Unit Cost</p>
                      <p className="text-sm font-medium text-foreground">
                        ${part?.unit_cost?.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>

                {part?.location && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
                    <Icon name="MapPin" size={14} />
                    <span>{part?.location}</span>
                  </div>
                )}

                {part?.supplier && (
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <Icon name="Truck" size={14} />
                    <span>Supplier: {part?.supplier}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PartsInventoryLookup;